import {
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  type FieldValue
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { functions } from '../firebase';
import { clientKeyManager } from '../db/helpers/clientKeyManager';
import {
  approveOrganizationInvitation as approveOrganizationInvitationCallable,
  rejectOrganizationInvitation as rejectOrganizationInvitationCallable,
  cancelOrganizationInvitation as cancelOrganizationInvitationCallable
} from './cloudFunctions';
import {
  Organization,
  OrganizationMembership,
  OrganizationInvitation,
  UserProfile,
  OrganizationPermission,
  UserPermissions,
  computeUserPermissions,
  DEFAULT_ORGANIZATION_PERMISSIONS
} from '../../../common/src/types/user';
/**
 * Safely convert a Firestore Timestamp (or anything already date-shaped) to a
 * Date. Falls back to "now" so callers never see an invalid date.
 */
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'object') {
    const stamp = value as { toDate?: () => Date };
    if (typeof stamp.toDate === 'function') return stamp.toDate();
  }
  return new Date();
};

import {
  OrganizationsRef_Collection,
  OrganizationRef_Document,
  OrganizationMembershipsRef_Collection,
  OrganizationMembershipRef_Document,
  OrganizationInvitationsRef_Collection,
  UserRef_Document,
  UsersRef_Collection
} from '../db';

/**
 * A new invitation as the client writes it: the id comes from Firestore and
 * `created_at` is the server sentinel rather than a Date.
 */
type NewOrganizationInvitation = Omit<OrganizationInvitation, 'id' | 'created_at'> & {
  created_at: FieldValue;
};

export class OrganizationService {
  
  // ============================================================================
  // ORGANIZATION MANAGEMENT
  // ============================================================================
  
  static async createOrganization(orgData: Omit<Organization, 'id' | 'created_at' | 'updated_at'>): Promise<string> {
    const clientKey = await clientKeyManager.getClientKey();
    const docRef = await addDoc(OrganizationsRef_Collection(clientKey), {
      ...orgData,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return docRef.id;
  }
  
  static async getOrganization(organizationId: string): Promise<Organization | null> {
    const docRef = OrganizationRef_Document(organizationId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();

      // Handle migration from old admin_id field to new admin_ids array
      let admin_ids = data.admin_ids;
      if (!admin_ids && data.admin_id) {
        admin_ids = [data.admin_id];
        // Migrate the data
        await updateDoc(docRef, {
          admin_ids: admin_ids,
          admin_id: null, // Remove old field
          updated_at: serverTimestamp()
        });
      }

      return {
        id: docSnap.id,
        ...data,
        admin_ids: admin_ids || [],
        created_at: toDate(data.created_at),
        updated_at: toDate(data.updated_at),
        billing: data.billing ? {
          ...data.billing,
          next_billing_date: toDate(data.billing.next_billing_date)
        } : undefined
      } as Organization;
    }

    return null;
  }
  
  static async updateOrganization(organizationId: string, updates: Partial<Organization>): Promise<Organization> {
    const docRef = OrganizationRef_Document(organizationId);

    // Clean updates to remove undefined values (Firestore doesn't allow undefined)
    const cleanUpdates: Record<string, unknown> = {};
    const incoming = updates as Record<string, unknown>;
    Object.keys(incoming).forEach(key => {
      const value = incoming[key];
      if (value !== undefined) {
        // For nested objects like branding, clean them too
        if (typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)) {
          const nested = value as Record<string, unknown>;
          const cleanedNested: Record<string, unknown> = {};
          Object.keys(nested).forEach(nestedKey => {
            if (nested[nestedKey] !== undefined) {
              cleanedNested[nestedKey] = nested[nestedKey];
            }
          });
          if (Object.keys(cleanedNested).length > 0) {
            cleanUpdates[key] = cleanedNested;
          }
        } else {
          cleanUpdates[key] = value;
        }
      }
    });

    await updateDoc(docRef, {
      ...cleanUpdates,
      updated_at: serverTimestamp()
    });

    // Fetch and return the updated organization
    const updatedDoc = await getDoc(docRef);
    if (!updatedDoc.exists()) {
      throw new Error('Organization not found after update');
    }

    const data = updatedDoc.data();
    return {
      id: updatedDoc.id,
      ...data,
      created_at: toDate(data.created_at),
      updated_at: toDate(data.updated_at),
      billing: {
        ...data.billing,
        next_billing_date: toDate(data.billing?.next_billing_date),
      },
    } as Organization;
  }
  
  static async deleteOrganization(organizationId: string): Promise<void> {
    const clientKey = await clientKeyManager.getClientKey();
    const batch = writeBatch(db);

    // Delete organization memberships
    const membershipsQuery = query(
      OrganizationMembershipsRef_Collection(clientKey),
      where('organization_id', '==', organizationId)
    );
    const memberships = await getDocs(membershipsQuery);
    memberships.forEach(doc => batch.delete(doc.ref));

    // Delete organization invitations
    const invitationsQuery = query(
      OrganizationInvitationsRef_Collection(clientKey),
      where('organization_id', '==', organizationId)
    );
    const invitations = await getDocs(invitationsQuery);
    invitations.forEach(doc => batch.delete(doc.ref));

    // Delete organization
    const orgRef = OrganizationRef_Document(organizationId);
    batch.delete(orgRef);

    await batch.commit();
  }
  
  static async getAllOrganizations(clientKey?: string): Promise<Organization[]> {
    clientKey = clientKey ?? await clientKeyManager.getClientKey();
    const querySnapshot = await getDocs(
      query(OrganizationsRef_Collection(clientKey), orderBy('name'))
    );
    return querySnapshot.docs.map(docSnap => {
      const data = docSnap.data();

      // Handle migration from old admin_id field to new admin_ids array
      let admin_ids = data.admin_ids;
      if (!admin_ids && data.admin_id) {
        admin_ids = [data.admin_id];
        // Migrate in background (don't await to avoid blocking)
        updateDoc(docSnap.ref, {
          admin_ids: admin_ids,
          admin_id: null,
          updated_at: serverTimestamp()
        }).catch(console.error);
      }

      return {
        id: docSnap.id,
        ...data,
        admin_ids: admin_ids || [],
        created_at: toDate(data.created_at),
        updated_at: toDate(data.updated_at),
        billing: data.billing ? {
          ...data.billing,
          next_billing_date: toDate(data.billing.next_billing_date)
        } : undefined
      } as Organization;
    });
  }
  
  // ============================================================================
  // MEMBERSHIP MANAGEMENT
  // ============================================================================
  
  static async addUserToOrganization(
    organizationId: string,
    userId: string,
    role: 'member' | 'admin' = 'member',
    invitedBy: string,
    permissions: OrganizationPermission[] = [],
    clientKey?: string
  ): Promise<string> {
    clientKey = await clientKeyManager.getClientKey();
    const docRef = await addDoc(OrganizationMembershipsRef_Collection(clientKey), {
      organization_id: organizationId,
      user_id: userId,
      role,
      joined_at: serverTimestamp(),
      permissions,
      status: 'active',
      invited_by: invitedBy
    });

    // Update user's organization membership
    const userRef = UserRef_Document(userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const memberships = userData.organization_memberships || [];

      await updateDoc(userRef, {
        organization_id: organizationId, // Set as primary org if first
        organization_memberships: [...memberships, docRef.id],
        org_link_state: 'linked', // Mark org as linked
        updated_at: serverTimestamp()
      });
    }

    return docRef.id;
  }
  
  static async removeUserFromOrganization(organizationId: string, userId: string): Promise<void> {
    const clientKey = await clientKeyManager.getClientKey();

    // Remove from admin_ids if they're an admin
    const org = await this.getOrganization(organizationId);
    if (org && (org.admin_ids || []).includes(userId)) {
      const updatedAdminIds = (org.admin_ids || []).filter(id => id !== userId);
      await this.updateOrganization(organizationId, { admin_ids: updatedAdminIds });
    }

    // Remove membership
    const membershipsQuery = query(
      OrganizationMembershipsRef_Collection(clientKey),
      where('organization_id', '==', organizationId),
      where('user_id', '==', userId)
    );
    const membershipDocs = await getDocs(membershipsQuery);

    for (const membershipDoc of membershipDocs.docs) {
      await deleteDoc(membershipDoc.ref);
    }

    // Update user profile
    const userRef = UserRef_Document(userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const updatedMemberships = (userData.organization_memberships || []).filter(
        (id: string) => !membershipDocs.docs.some(doc => doc.id === id)
      );

      const noMoreMemberships = updatedMemberships.length === 0;
      await updateDoc(userRef, {
        organization_id: noMoreMemberships ? null : userData.organization_id,
        organization_memberships: updatedMemberships,
        org_link_state: noMoreMemberships ? 'unlinked' : userData.org_link_state,
        updated_at: serverTimestamp()
      });
    }
  }
  
  static async getUserMembership(organizationId: string, userId: string): Promise<OrganizationMembership | null> {
    const clientKey = await clientKeyManager.getClientKey();
    const membershipsQuery = query(
      OrganizationMembershipsRef_Collection(clientKey),
      where('organization_id', '==', organizationId),
      where('user_id', '==', userId)
    );

    const querySnapshot = await getDocs(membershipsQuery);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        joined_at: toDate(data.joined_at),
        approved_at: toDate(data.approved_at)
      } as OrganizationMembership;
    }

    return null;
  }
  
  static async getOrganizationMembers(organizationId: string): Promise<OrganizationMembership[]> {
    const clientKey = await clientKeyManager.getClientKey();
    const membershipsQuery = query(
      OrganizationMembershipsRef_Collection(clientKey),
      where('organization_id', '==', organizationId),
      orderBy('joined_at', 'desc')
    );

    const querySnapshot = await getDocs(membershipsQuery);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        joined_at: toDate(data.joined_at),
        approved_at: toDate(data.approved_at)
      } as OrganizationMembership;
    });
  }

  static async getOrganizationMembersPaginated(
    organizationId: string,
    pageSize: number = 25,
    lastDoc?: QueryDocumentSnapshot,
    clientKey?: string
  ): Promise<{
    members: OrganizationMembership[];
    lastVisible: QueryDocumentSnapshot | null;
    hasMore: boolean;
  }> {
    const key = clientKey ?? await clientKeyManager.getClientKey();
    let membershipsQuery = query(
      OrganizationMembershipsRef_Collection(key),
      where('organization_id', '==', organizationId),
      orderBy('joined_at', 'desc'),
      limit(pageSize + 1) // Fetch one extra to determine if there are more
    );

    // If we have a cursor, start after it
    if (lastDoc) {
      membershipsQuery = query(
        OrganizationMembershipsRef_Collection(key),
        where('organization_id', '==', organizationId),
        orderBy('joined_at', 'desc'),
        startAfter(lastDoc),
        limit(pageSize + 1)
      );
    }

    const querySnapshot = await getDocs(membershipsQuery);    const docs = querySnapshot.docs;

    // Check if there are more results
    const hasMore = docs.length > pageSize;

    // Get only the requested page size
    const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;

    const members = pageDocs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        joined_at: toDate(data.joined_at),
        approved_at: toDate(data.approved_at)
      } as OrganizationMembership;
    });

    return {
      members,
      lastVisible: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
      hasMore
    };
  }
  
  static async updateUserPermissions(
    organizationId: string,
    userId: string,
    permissions: OrganizationPermission[],
    clientKey?: string
  ): Promise<void> {
    // Update membership permissions
    const membership = await this.getUserMembership(organizationId, userId);
    if (membership) {
      const membershipRef = OrganizationMembershipRef_Document(membership.id);
      await updateDoc(membershipRef, {
        permissions
      });
    }

    // Update user's enabled permissions
    const userRef = UserRef_Document(userId);
    await updateDoc(userRef, {
      enabled_permissions: permissions,
      updated_at: serverTimestamp()
    });
  }
  
  // ============================================================================
  // PERMISSION SYSTEM
  // ============================================================================
  
  static async getUserPermissions(userId: string): Promise<UserPermissions | null> {
    const userRef = UserRef_Document(userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return null;

    const userData = userDoc.data() as UserProfile;

    // Get organization permissions if user belongs to one
    let organizationPermissions: OrganizationPermission[] = [];

    if (userData.organization_id) {
      const org = await this.getOrganization(userData.organization_id);
      organizationPermissions = org?.permissions || [];
    }

    return computeUserPermissions(userData, organizationPermissions);
  }
  
  static async checkUserPermission(
    userId: string,
    permission: keyof UserPermissions['effective_permissions'],
    clientKey?: string
  ): Promise<boolean> {
    const userPermissions = await this.getUserPermissions(userId);
    return userPermissions?.effective_permissions[permission] || false;
  }

  static async setOrganizationPermissions(
    organizationId: string,
    permissions: OrganizationPermission[],
    clientKey?: string
  ): Promise<void> {
    const orgRef = OrganizationRef_Document(organizationId);
    await updateDoc(orgRef, {
      permissions,
      updated_at: serverTimestamp()
    });
  }
  
  // ============================================================================
  // INVITATION SYSTEM
  // ============================================================================
  
  static async createInvitation(
    organizationId: string,
    invitedBy: string,
    invitedEmail?: string,
    invitedUserId?: string,
    customCode?: string,
    clientKey?: string
  ): Promise<string> {
    clientKey = await clientKeyManager.getClientKey();
    // Check if user is already a member (only if user exists)
    if (invitedEmail && !invitedUserId) {
      // Look up user by email to get their userId
      const usersQuery = query(
        UsersRef_Collection(clientKey),
        where('email', '==', invitedEmail)
      );
      const userDocs = await getDocs(usersQuery);

      // Only check membership if user exists
      if (!userDocs.empty) {
        const userDoc = userDocs.docs[0];
        const userId = userDoc.id;
        const userData = userDoc.data();

        // Check 1: User document has organization_id field
        if (userData.organization_id === organizationId) {
          throw new Error('This user is already a member of the organization');
        }

        // Check 2: User document has this org in their organization_memberships array
        if (userData.organization_memberships && Array.isArray(userData.organization_memberships)) {
          const membershipDocs = await Promise.all(
            userData.organization_memberships.map(membershipId =>
              getDoc(OrganizationMembershipRef_Document(membershipId))
            )
          );

          const isAlreadyMember = membershipDocs.some(
            membershipDoc => membershipDoc.exists() && membershipDoc.data()?.organization_id === organizationId
          );

          if (isAlreadyMember) {
            throw new Error('This user is already a member of the organization');
          }
        }

        // Check 3: Organization memberships collection
        const memberCheckQuery = query(
          OrganizationMembershipsRef_Collection(clientKey),
          where('organization_id', '==', organizationId),
          where('user_id', '==', userId)
        );
        const memberCheck = await getDocs(memberCheckQuery);
        if (!memberCheck.empty) {
          throw new Error('This user is already a member of the organization');
        }
      }
    } else if (invitedUserId) {
      // If we have userId directly, check user doc and memberships
      const userDoc = await getDoc(UserRef_Document(invitedUserId));

      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Check 1: User document has organization_id field
        if (userData.organization_id === organizationId) {
          throw new Error('This user is already a member of the organization');
        }

        // Check 2: User document has this org in their organization_memberships array
        if (userData.organization_memberships && Array.isArray(userData.organization_memberships)) {
          const membershipDocs = await Promise.all(
            userData.organization_memberships.map((membershipId: string) =>
              getDoc(OrganizationMembershipRef_Document(membershipId))
            )
          );

          const isAlreadyMember = membershipDocs.some(
            membershipDoc => membershipDoc.exists() && membershipDoc.data()?.organization_id === organizationId
          );

          if (isAlreadyMember) {
            throw new Error('This user is already a member of the organization');
          }
        }
      }

      // Check 3: Organization memberships collection
      const memberCheckQuery = query(
        OrganizationMembershipsRef_Collection(clientKey),
        where('organization_id', '==', organizationId),
        where('user_id', '==', invitedUserId)
      );
      const memberCheck = await getDocs(memberCheckQuery);
      if (!memberCheck.empty) {
        throw new Error('This user is already a member of the organization');
      }
    }

    // Expire any existing pending invitations for the same email/user
    const existingInvitationsQuery = query(
      OrganizationInvitationsRef_Collection(clientKey),
      where('organization_id', '==', organizationId),
      where('status', '==', 'pending'),
      ...(invitedEmail ? [where('invited_email', '==', invitedEmail)] : [])
    );

    const existingInvitations = await getDocs(existingInvitationsQuery);
    const batch = writeBatch(db);

    existingInvitations.docs.forEach((invDoc) => {
      batch.update(invDoc.ref, { status: 'expired' });
    });

    if (!existingInvitations.empty) {
      await batch.commit();
    }

    const inviteCode = customCode || this.generateInviteCode();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 30); // 30 days expiry

    // Build invitation data, only including defined fields — Firestore rejects
    // undefined, so `invited_user_id` / `invited_email` are added below.
    const invitationData: NewOrganizationInvitation = {
      organization_id: organizationId,
      invited_by: invitedBy,
      invite_code: inviteCode,
      status: 'pending',
      expires_at: expiresAt,
      created_at: serverTimestamp()
    };

    // Only add optional fields if they have values
    if (invitedUserId) {
      invitationData.invited_user_id = invitedUserId;
    }
    if (invitedEmail) {
      invitationData.invited_email = invitedEmail;
    }

    const docRef = await addDoc(OrganizationInvitationsRef_Collection(clientKey), invitationData);

    return docRef.id;
  }
  
  static async getInvitationByCode(inviteCode: string): Promise<OrganizationInvitation | null> {
    // Try to get client key from auth, but fall back to default if user not signed in
    // Invitations should be viewable before authentication
    let clientKey: string = 'athleto'; // Default fallback
    try {
      const fetchedKey = await clientKeyManager.getClientKey();
      // Ensure we have a valid string, not null/undefined
      if (fetchedKey && typeof fetchedKey === 'string') {
        clientKey = fetchedKey;
      }
    } catch {
      // Invitations are readable before sign-in, so fall back to the default
      // client key rather than failing the lookup.
    }
    const invitationsQuery = query(
      OrganizationInvitationsRef_Collection(clientKey),
      where('invite_code', '==', inviteCode),
      where('status', '==', 'pending')
    );

    const querySnapshot = await getDocs(invitationsQuery);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        expires_at: toDate(data.expires_at),
        created_at: toDate(data.created_at),
        accepted_at: toDate(data.accepted_at),
        approved_at: toDate(data.approved_at),
        rejected_at: toDate(data.rejected_at)
      } as OrganizationInvitation;
    }

    return null;
  }
  
  static async acceptInvitation(
    inviteCode: string,
    userId: string,
    userEmail: string,
    clientKey?: string
  ): Promise<{ success: boolean; outcome: 'linked' | 'pending'; requestId?: string; organizationId?: string }> {
    const acceptInvite = httpsCallable(functions, 'acceptInviteWithPolicy');
    const result = await acceptInvite({
      inviteType: 'organization',
      code: inviteCode,
      idempotencyKey: `org-${userId}-${inviteCode}`,
    });
    return result.data as { success: boolean; outcome: 'linked' | 'pending'; requestId?: string; organizationId?: string };
  }

  static async requestOrgAccess(organizationId: string, idempotencyKey?: string): Promise<{ requestId: string; status: string; existing?: boolean }> {
    const callable = httpsCallable(functions, 'requestOrgAccess');
    const result = await callable({ organizationId, idempotencyKey });
    return result.data as { requestId: string; status: string; existing?: boolean };
  }
  
  static async approveInvitation(
    invitationId: string,
    approvedBy: string,
    userId: string,
    clientKey?: string
  ): Promise<void> {
    await approveOrganizationInvitationCallable({
      invitationId,
      userId
    });
  }
  
  static async rejectInvitation(
    invitationId: string,
    rejectedBy: string,
    reason?: string,
    clientKey?: string
  ): Promise<void> {
    await rejectOrganizationInvitationCallable({
      invitationId,
      reason
    });
  }

  static async cancelInvitation(invitationId: string): Promise<void> {
    await cancelOrganizationInvitationCallable({
      invitationId
    });
  }

  static async getOrganizationInvitations(organizationId: string): Promise<OrganizationInvitation[]> {
    const clientKey = await clientKeyManager.getClientKey();
    const invitationsQuery = query(
      OrganizationInvitationsRef_Collection(clientKey),
      where('organization_id', '==', organizationId),
      orderBy('created_at', 'desc')
    );

    const querySnapshot = await getDocs(invitationsQuery);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        expires_at: toDate(data.expires_at),
        created_at: toDate(data.created_at),
        accepted_at: toDate(data.accepted_at),
        approved_at: toDate(data.approved_at),
        rejected_at: toDate(data.rejected_at)
      } as OrganizationInvitation;
    });
  }
  
  // ============================================================================
  // UTILITY METHODS
  // ============================================================================
  
  private static generateInviteCode(): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }
  
  static async isUserOrganizationAdmin(organizationId: string, userId: string): Promise<boolean> {
    const org = await this.getOrganization(organizationId);
    if (!org) return false;

    return (org.admin_ids || []).includes(userId);
  }

  static async getUserOrganizations(userId: string): Promise<Organization[]> {
    const clientKey = await clientKeyManager.getClientKey();
    const membershipsQuery = query(
      OrganizationMembershipsRef_Collection(clientKey),
      where('user_id', '==', userId),
      where('status', '==', 'active')
    );

    const memberships = await getDocs(membershipsQuery);    const orgIds = memberships.docs.map(doc => doc.data().organization_id);

    const organizations: Organization[] = [];
    for (const orgId of orgIds) {
      const org = await this.getOrganization(orgId);
      if (org) organizations.push(org);
    }

    return organizations;
  }
  
  static getDefaultPermissionsForPlan(planType: string): OrganizationPermission[] {
    return DEFAULT_ORGANIZATION_PERMISSIONS[planType] || DEFAULT_ORGANIZATION_PERMISSIONS.free;
  }

  // ============================================================================
  // ADMIN MANAGEMENT
  // ============================================================================

  static async addOrganizationAdmin(organizationId: string, userId: string): Promise<void> {
    // Check if user is already admin of another organization
    const userOrgs = await this.getUserOrganizations(userId);
    for (const org of userOrgs) {
      if (org.id !== organizationId && (org.admin_ids || []).includes(userId)) {
        throw new Error('User is already an admin of another organization. Users can only be admin of one organization.');
      }
    }

    const org = await this.getOrganization(organizationId);
    if (!org) throw new Error('Organization not found');

    if ((org.admin_ids || []).includes(userId)) {
      throw new Error('User is already an admin of this organization');
    }

    // Add user to admin list
    const updatedAdminIds = [...(org.admin_ids || []), userId];
    await this.updateOrganization(organizationId, { admin_ids: updatedAdminIds });

    // Ensure user has organization membership with admin role
    const membership = await this.getUserMembership(organizationId, userId);
    if (!membership) {
      // Add as admin member with full permissions
      await this.addUserToOrganization(
        organizationId,
        userId,
        'admin',
        'system', // Added by system when promoting to admin
        org.permissions
      );
    } else {
      // Update existing membership to admin role
      const membershipRef = OrganizationMembershipRef_Document(membership.id);
      await updateDoc(membershipRef, {
        role: 'admin',
        permissions: org.permissions
      });
    }

    // Update user's role to 'school_admin' if they're not already school_admin or super_admin
    // Also ensure org_link_state is set to 'linked'
    const userRef = UserRef_Document(userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const updates: Record<string, any> = {
        org_link_state: 'linked',
        updated_at: serverTimestamp()
      };
      if (userData.role !== 'school_admin' && userData.role !== 'super_admin') {
        updates.role = 'school_admin';
      }
      await updateDoc(userRef, updates);
    }
  }

  static async removeOrganizationAdmin(organizationId: string, userId: string): Promise<void> {
    const org = await this.getOrganization(organizationId);
    if (!org) throw new Error('Organization not found');

    if (!(org.admin_ids || []).includes(userId)) {
      throw new Error('User is not an admin of this organization');
    }

    // Don't allow removing the last admin
    if ((org.admin_ids || []).length === 1) {
      throw new Error('Cannot remove the last admin from an organization');
    }

    // Remove user from admin list
    const updatedAdminIds = (org.admin_ids || []).filter(id => id !== userId);
    await this.updateOrganization(organizationId, { admin_ids: updatedAdminIds });

    // Update membership to regular member role
    const membership = await this.getUserMembership(organizationId, userId);
    if (membership) {
      const membershipRef = OrganizationMembershipRef_Document(membership.id);
      await updateDoc(membershipRef, {
        role: 'member',
        permissions: [] // Remove admin permissions
      });
    }

    // Check if user is admin of any other organizations
    const userOrgs = await this.getUserOrganizations(userId);
    const isAdminElsewhere = userOrgs.some(userOrg =>
      userOrg.id !== organizationId && (userOrg.admin_ids || []).includes(userId)
    );

    // If not admin elsewhere, downgrade user role
    if (!isAdminElsewhere) {
      const userRef = UserRef_Document(userId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Only downgrade from school_admin role, not super_admin
        if (userData.role === 'school_admin') {
          await updateDoc(userRef, {
            role: 'athlete', // Default role
            updated_at: serverTimestamp()
          });
        }
      }
    }
  }

  static async getOrganizationAdmins(organizationId: string): Promise<UserProfile[]> {
    const org = await this.getOrganization(organizationId);
    if (!org) return [];

    const adminIds = org.admin_ids || [];
    const admins: UserProfile[] = [];
    for (const adminId of adminIds) {
      const userRef = UserRef_Document(adminId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        admins.push({
          uid: userDoc.id,
          ...userData,
          created_at: toDate(userData.created_at),
          updated_at: toDate(userData.updated_at)
        } as UserProfile);
      }
    }

    return admins;
  }
}
